import CloseIcon from '@mui/icons-material/Close'
import { useState } from 'react'
import CopyButton from './CopyButton'
import { borderRadius } from '@store/constant'

import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Link,
  List,
  ListItem,
  Typography,
} from '@mui/material'

interface Props {
  conciergeId: string
}

function InstallNow({ conciergeId }: Props) {
  const [open, setOpen] = useState(false)

  if (!conciergeId) {
    return (
      <Typography variant='h6' color='error'>
        Something went wrong. Please, try again later
      </Typography>
    )
  }

  const links = [
    {
      url: 'https://help.webflow.com/hc/en-us/articles/33961332238611-Custom-code-embed',
      text: 'Webflow',
    },
    {
      url: 'https://digital-wisdom.notion.site/Square-Space-Installation-21d38a3c73c980eb8438f3493b591c24',
      text: 'Squarespace',
    },
    {
      url: 'https://websitebuildersupport.web.com/article/1515-html-injection',
      text: 'Web.com',
    },
  ]

  function getLinkItems() {
    return links.map(({ url, text }) => (
      <ListItem
        key={url}
        sx={{
          padding: '2px 0',
        }}
      >
        <Link
          href={url}
          target='_blank'
          rel='noopener noreferrer'
          sx={{
            color: '#D59D20',
            textDecorationColor: '#D59D20',
            '&:hover': {
              opacity: '0.8',
            },
          }}
        >
          {text}
        </Link>
      </ListItem>
    ))
  }

  const code = `<script type="module" crossorigin src="https://concierge.digitalwisdom.ai/chat-v1.js"></script>
<web-concierge id="${conciergeId}"></web-concierge>`

  function onClose() {
    setOpen(false)
  }
  const dialog = (
    <Dialog onClose={onClose} open={open}>
      <DialogContent sx={{ padding: 4 }}>
        <IconButton
          aria-label='close'
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'grey.500',
          }}
        >
          <CloseIcon />
        </IconButton>
        <Typography variant='h3' sx={{ mb: 3 }}>
          Install Web Wisdom
        </Typography>
        <Typography variant='h4' sx={{ mb: 0.5 }}>
          WordPress instructions:
        </Typography>
        <Typography gutterBottom sx={{ mb: 3 }}>
          Follow{' '}
          <Link
            href='https://www.webwisdom.ai/install-a-plugin'
            target='_blank'
            rel='noopener noreferrer'
            sx={{
              color: '#D59D20',
              textDecorationColor: '#D59D20',
              '&:hover': {
                opacity: '0.8',
              },
            }}
          >
            these instructions to install our custom WordPress plugin.
          </Link>
        </Typography>
        <Typography variant='h4' sx={{ mb: 0.5 }}>
          Wix instructions:
        </Typography>
        <Typography gutterBottom sx={{ mb: 3 }}>
          <Link
            href='https://wix.to/hYzcfWu'
            target='_blank'
            rel='noopener noreferrer'
            sx={{
              color: '#D59D20',
              textDecorationColor: '#D59D20',
              '&:hover': {
                opacity: '0.8',
              },
            }}
          >
            Install on a Wix website
          </Link>
        </Typography>
        <Typography variant='h4' sx={{ mb: 0.5 }}>
          For all other platforms:
        </Typography>
        <Typography gutterBottom sx={{ mb: 2 }}>
          Copy and place this code in your website&apos;s HTML between the{' '}
          <code
            style={{
              backgroundColor: '#f5f5f5',
              padding: '2px 4px',
              borderRadius: '4px',
            }}
          >
            &lt;body&gt;&lt;/body&gt;
          </code>
          tags to install Web Wisdom. Once embedded, it will be live and ready
          to assist your users.
        </Typography>
        <div
          style={{
            position: 'relative',
            backgroundColor: '#F0F0F0',
            padding: '24px 16px 16px ',
            borderRadius: `${borderRadius}px`,
          }}
        >
          <CopyButton code={code} />
          <pre
            style={{
              margin: 0,
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              fontSize: '12px',
            }}
          >
            <code>{code}</code>
          </pre>
        </div>
        <Typography gutterBottom sx={{ marginTop: 4, marginBottom: 0 }}>
          Need help? Check out these guides for embedding scripts:
        </Typography>
        <List>{getLinkItems()}</List>
      </DialogContent>
    </Dialog>
  )

  return (
    <>
      {dialog}

      <Button
        id='install-now'
        variant='contained'
        onClick={() => setOpen(!open)}
        sx={{ minWidth: 130 }}
      >
        Install On Website
      </Button>
    </>
  )
}

export default InstallNow
